import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  BLUE,
  cardSx,
  INK,
  MUTED,
  PROMOTER,
  PURPLE,
  SECONDARY,
} from 'client/modules/Analyze/analyzeTheme';
import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

const STEP_COLORS = [BLUE, PURPLE, PROMOTER];

const deliveryActionMessages = defineMessages({
  shown: { id: 'Person.status.shown', defaultMessage: 'Survey shown' },
  responded: { id: 'Person.status.responded', defaultMessage: 'Survey responded' },
  commented: { id: 'Person.status.commented', defaultMessage: 'Survey commented' },
  sent: { id: 'Person.status.sent', defaultMessage: 'Survey sent' },
  opened: { id: 'Person.status.opened', defaultMessage: 'Survey opened' },
  scheduled: { id: 'Person.status.scheduled', defaultMessage: 'Survey scheduled' },
  throttled: { id: 'Person.status.throttled', defaultMessage: 'Survey throttled' },
  sampled_out: { id: 'Person.status.sampled_out', defaultMessage: 'Omitted by sampling' },
  audience_excluded: {
    id: 'Person.status.audience_excluded',
    defaultMessage: 'Excluded by audience',
  },
  deferred: { id: 'Person.status.deferred', defaultMessage: 'Survey deferred' },
  bounced: { id: 'Person.status.bounced', defaultMessage: 'Email bounced' },
  blocked: { id: 'Person.status.blocked', defaultMessage: 'Email blocked' },
  unsubscribed: { id: 'Person.status.unsubscribed', defaultMessage: 'Email unsubscribed' },
});

function useActionLabel(action, fallbackLabel) {
  const { formatMessage } = useIntl();
  const msg = deliveryActionMessages[action];
  return msg ? formatMessage(msg) : fallbackLabel;
}

function FunnelStep({ step, color, barWidth }) {
  const { formatNumber } = useIntl();
  const label = useActionLabel(step.action, step.label);
  const width = Math.max(0, Math.min(100, barWidth));

  return (
    <Box sx={{ flex: 1, textAlign: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.9,
          fontSize: 13,
          fontWeight: 600,
          color: SECONDARY,
        }}
      >
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
        {label}
      </Box>
      <Typography
        sx={{
          fontSize: 42,
          fontWeight: 800,
          color: INK,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-1.5px',
          mt: 1,
        }}
      >
        {formatNumber(step.count)}
      </Typography>
      <Box
        sx={{
          height: 8,
          bgcolor: '#EEF1F5',
          borderRadius: '4px',
          mx: 1,
          mt: 1.75,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ width: `${width}%`, height: '100%', bgcolor: color }} />
      </Box>
      <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>
        {step.rateFromPreviousPercent != null ? (
          <FormattedMessage
            id="analyze.delivery.conversionRate"
            defaultMessage="{rate}% of previous"
            values={{
              rate: <b style={{ color }}>{formatNumber(step.rateFromPreviousPercent)}%</b>,
            }}
          />
        ) : (
          <FormattedMessage id="analyze.delivery.ofAudience" defaultMessage="100% of audience" />
        )}
      </Typography>
    </Box>
  );
}

FunnelStep.propTypes = {
  step: PropTypes.shape({
    action: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    rateFromPreviousPercent: PropTypes.number,
  }).isRequired,
  color: PropTypes.string.isRequired,
  barWidth: PropTypes.number.isRequired,
};

function SecondaryMetric({ item }) {
  const { formatNumber } = useIntl();
  const label = useActionLabel(item.action, item.label);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: '#F8FAFC',
        border: '1px solid #ECEFF3',
        borderRadius: '8px',
        px: 1.75,
        py: 1.1,
        fontSize: 13,
        color: SECONDARY,
      }}
    >
      {label}{' '}
      <b style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(item.count)}</b>
    </Box>
  );
}

SecondaryMetric.propTypes = {
  item: PropTypes.shape({
    action: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
};

function DeliveryFunnelCards({ funnel, secondary }) {
  const firstCount = funnel[0]?.count || 0;

  return (
    <Box sx={{ ...cardSx, p: '28px 30px', mb: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {funnel.map((step, index) => (
          <React.Fragment key={step.action}>
            <FunnelStep
              step={step}
              color={STEP_COLORS[index % STEP_COLORS.length]}
              barWidth={firstCount > 0 ? (step.count / firstCount) * 100 : 0}
            />
            {index < funnel.length - 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75 }}>
                <ArrowForwardIcon sx={{ color: '#CBD3DE' }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
      {secondary?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            flexWrap: 'wrap',
            mt: 3.25,
            pt: 2.75,
            borderTop: '1px solid #F1F4F8',
          }}
        >
          {secondary.map(item => (
            <SecondaryMetric key={item.action} item={item} />
          ))}
        </Box>
      )}
    </Box>
  );
}

DeliveryFunnelCards.propTypes = {
  funnel: PropTypes.arrayOf(PropTypes.object).isRequired,
  secondary: PropTypes.arrayOf(PropTypes.object),
};

export default DeliveryFunnelCards;
